import React from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InsertLinkRoundedIcon from '@mui/icons-material/InsertLinkRounded';

import { formatCurrency, safeNumber } from './financialReportFormatters';

export default function FinancialReportDetailsTable({
    data,
    loading,
    expandedPeriods,
    expandedAccounts,
    onTogglePeriod,
    onToggleAccount,
}) {
    return (
        <Card>
            <CardHeader title="Detailansicht" subheader="Periode → Konto → Beleg" />
            <Divider />
            <CardContent sx={{ p: 0 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell width={64} />
                            <TableCell>Bezeichnung</TableCell>
                            <TableCell align="right">Einnahmen</TableCell>
                            <TableCell align="right">Ausgaben</TableCell>
                            <TableCell align="right">Ergebnis</TableCell>
                            <TableCell align="center">Beleg</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(data?.periods || []).map((period) => {
                            const isExpanded = expandedPeriods.has(period.key);
                            const totals = period.totals || {};
                            return (
                                <React.Fragment key={period.key}>
                                    <TableRow hover onClick={() => onTogglePeriod(period.key)} sx={{ cursor: 'pointer' }}>
                                        <TableCell>
                                            {isExpanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="subtitle1">{period.label}</Typography>
                                        </TableCell>
                                        <TableCell align="right">{formatCurrency(safeNumber(totals.income))}</TableCell>
                                        <TableCell align="right">{formatCurrency(-safeNumber(totals.expense))}</TableCell>
                                        <TableCell align="right">{formatCurrency(safeNumber(totals.result))}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                    {isExpanded &&
                                        (period.accounts || []).map((account) => {
                                            const accountKey = `${account.accountNumber || ''}-${account.accountName || ''}`;
                                            const accountExpanded = expandedAccounts.has(`${period.key}::${accountKey}`);
                                            const sign = account.nature === 'EXPENSE' ? -1 : 1;
                                            return (
                                                <React.Fragment key={`${period.key}-${accountKey}`}>
                                                    <TableRow
                                                        hover
                                                        onClick={() => onToggleAccount(period.key, accountKey)}
                                                        sx={{ cursor: 'pointer', backgroundColor: 'action.hover' }}
                                                    >
                                                        <TableCell />
                                                        <TableCell>
                                                            <Typography variant="body1">
                                                                {[account.accountNumber, account.accountName]
                                                                    .filter(Boolean)
                                                                    .join(' ') || 'Unbekanntes Konto'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {account.nature === 'INCOME' &&
                                                                formatCurrency(sign * safeNumber(account.total))}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {account.nature === 'EXPENSE' &&
                                                                formatCurrency(sign * safeNumber(account.total))}
                                                        </TableCell>
                                                        <TableCell align="right" />
                                                        <TableCell />
                                                    </TableRow>
                                                    {accountExpanded &&
                                                        (account.documents || []).map((doc, idx) => (
                                                            <TableRow key={`${period.key}-${accountKey}-${doc.id || doc.number || idx}`}>
                                                                <TableCell />
                                                                <TableCell sx={{ pl: 4 }}>
                                                                    <Stack spacing={0.5}>
                                                                        <Typography variant="body2" fontWeight={600}>
                                                                            {doc.number || 'Beleg'}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {doc.description || ''}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            Kostenstelle: {doc.costCenter || 'Allgemein'}
                                                                        </Typography>
                                                                    </Stack>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {account.nature === 'INCOME' &&
                                                                        formatCurrency(sign * safeNumber(doc.amount))}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {account.nature === 'EXPENSE' &&
                                                                        formatCurrency(sign * safeNumber(doc.amount))}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {doc.date || ''}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    {doc.downloadUrl ? (
                                                                        <Tooltip title="Originalbeleg öffnen">
                                                                            <Button
                                                                                variant="text"
                                                                                size="small"
                                                                                href={doc.downloadUrl}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                startIcon={<InsertLinkRoundedIcon />}
                                                                            >
                                                                                Öffnen
                                                                            </Button>
                                                                        </Tooltip>
                                                                    ) : (
                                                                        <Typography variant="caption" color="text.disabled">
                                                                            Kein Download
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                </React.Fragment>
                                            );
                                        })}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
                {!data?.periods?.length && !loading && (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">Keine Daten für den ausgewählten Zeitraum.</Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}
